import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlayCircle, Send, CheckCircle2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import { DownloadButton } from "@/components/shared/DownloadButton";
import apiClient from "@/lib/api";

export default function Tutorials() {
  const { t } = useTranslation();
  const videos = t("tutorials.videos", { returnObjects: true });
  const videosConstruction = t("tutorials.videos_construction", { returnObjects: true });
  const guides = t("tutorials.guides", { returnObjects: true });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/contact", { ...form, subject: "Question sur un tutoriel" });
      setSent(true);
      toast.success(t("common.success_generic"));
    } catch {
      toast.error(t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tutorials-page">
      <SEO title={`${t("tutorials.hero_title")} — Fastority`} description={t("tutorials.hero_subtitle")} path="tutorials" />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("tutorials.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70">{t("tutorials.hero_subtitle")}</p>
          <DownloadButton
            testId="tutorials-download-cta"
            className="mt-8 inline-flex items-center gap-1.5 bg-coral text-white text-sm font-semibold px-6 py-3.5 rounded-full hover:scale-[1.02] transition-transform duration-300"
          >
            {t("common.download_free")}
            <ChevronRight size={15} />
          </DownloadButton>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("tutorials.videos_title")}</h2>
            <p className="mt-2 text-slate-600">{t("tutorials.videos_subtitle")}</p>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {videos.map((v, i) => (
              <SectionReveal key={v.title} delay={i * 0.08}>
                <div data-testid={`tutorial-video-${i}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden group">
                  {v.youtube_id ? (
                    <div className="aspect-video">
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${v.youtube_id}`}
                        title={v.title}
                        frameBorder="0"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`tutorial-video-iframe-${i}`}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate900 flex items-center justify-center relative">
                      <PlayCircle className="text-white/70 group-hover:text-coral group-hover:scale-110 transition-all duration-300" size={36} />
                      <span className="absolute bottom-2 right-3 text-xs font-medium text-white/60">{v.duration}</span>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-sm text-slate900">{v.title}</h3>
                    <p className="text-xs text-slate-600 mt-1">{v.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("tutorials.videos_construction_title")}</h2>
          </SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {videosConstruction.map((v, i) => (
              <SectionReveal key={v.title + i} delay={i * 0.05}>
                <div data-testid={`tutorial-video-construction-${i}`} className="bg-offwhite rounded-2xl border border-gray-100 overflow-hidden opacity-80">
                  <div className="aspect-video bg-slate900/80 flex items-center justify-center relative">
                    <PlayCircle className="text-white/40" size={32} />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-sm text-slate900">{v.title}</h3>
                    <p className="text-xs text-slate-600 mt-1">{v.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("tutorials.guides_title")}</h2>
          </SectionReveal>
          <div className="space-y-5">
            {guides.map((g, i) => (
              <SectionReveal key={g.title} delay={i * 0.08}>
                <div data-testid={`tutorial-guide-${i}`} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-heading font-bold text-lg text-slate900 mb-1">{g.title}</h3>
                  <p className="text-sm text-slate-600">{g.excerpt}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-xl mx-auto px-5 sm:px-8">
          <SectionReveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate900">{t("tutorials.contact_title")}</h2>
            <p className="mt-2 text-slate-600">{t("tutorials.contact_subtitle")}</p>
          </SectionReveal>

          {sent ? (
            <div className="text-center bg-emeraldgreen/10 border border-emeraldgreen/30 rounded-3xl p-10">
              <CheckCircle2 className="mx-auto mb-3 text-emeraldgreen" size={32} />
              <p className="font-heading font-bold text-slate900" data-testid="tutorials-form-success">{t("common.success_generic")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="tutorials-contact-form" className="bg-white rounded-3xl p-8 border border-gray-100 space-y-4">
              <input required data-testid="tutorials-form-name" placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <input required type="email" data-testid="tutorials-form-email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <textarea required data-testid="tutorials-form-message" placeholder={t("common.message")} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
              <button type="submit" disabled={loading} data-testid="tutorials-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-slate900 text-white font-semibold px-6 py-4 rounded-full hover:bg-coral transition-colors duration-300 disabled:opacity-60">
                {loading ? t("common.sending") : t("common.send")} <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
